export const STACKED_LIST_CONTROLLER = "StackedListController"
export const NESTED_LIST_CONTROLLER = "NestedListController"
const MODAL_CONTROLLERS = [
    STACKED_LIST_CONTROLLER,
    NESTED_LIST_CONTROLLER
]


/*----------------------------------------
 * CONFIG RELATED
 */

export function selectComponentTitle (state) {
    return state.config.componentTitle || "Archives"
}

export function selectDefaultPhrase (state) {
    return state.config.defaultPhrase || "All"
}

export function selectDataEndpoint (state) {
    return state.config.endpoint
}

export function selectKeywordEndpoint (state) {
    return state.config.keywordEndpoint
}


export function selectBarLinks (state) {
    return state.config.barLinks
}


/*----------------------------------------
 * SORT RELATED
 */

export function selectActiveSort (state) {
    var activeSortValue = state.data.activeSort
    var activeSort = state.config.sorts.find( s => s.value === activeSortValue )
    return activeSort || state.config.sorts[0]
}


export function selectSorts (state) {
    return state.config.sorts
}


/*----------------------------------------
 * KEYWORD RELATED
 */

export function selectKeywordValue (state) {
  var activeFilters = selectActiveFilters(state)
  for ( let filter of activeFilters ) {
    if (filter.param === "keywords") {
      return filter.value
    }
  }
  return "" 
}

export function selectKeywordText (state) {
  return state.ui.keywordText
}

/* As of 2018-08-28 see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions */
function escapeRegExp (string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
}

export function selectKeywordCompletions (state) {
    var searchString = escapeRegExp(selectKeywordText(state))
    var filters = state.data.filters

    var reFromBeginning = new RegExp(`^${searchString}`, 'i');
    var reAnywhere = new RegExp(searchString, 'i') 

    var maxMatches = 10
    var scoreFromBeginning = 1000
    var scoreAnyWhere = 100

    var topMatches = []
    var topScores = []
    var lowScore = 50 

    for ( let filter of filters ) {
        var { name, id, paramName } = filter
        // we don't want completions that are already active
        if (selectIsFilterActive(state, id)) {
            continue;
        }
        if (paramName === "Keyword") {
            continue;
        }

        var charCount = name.length
        var matchFromBeginning = reFromBeginning.test(name)
        var matchAnywhere = matchFromBeginning ? true : reAnywhere.test(name) 
        
        var score = -1 * charCount 
        if (matchFromBeginning) {
            score = scoreFromBeginning - charCount
        }
        else if (matchAnywhere) {
            score = scoreAnyWhere - charCount / 10
        }

        if ( filter.param !== "rel" && (matchFromBeginning || matchAnywhere)) {
            score = score + 1000
        }

        // if this score is better than the 10th best we have found,
        // check until we find the index to insert
        var highScore = topScores[0] || 0 
        if (score > lowScore) {
            var i = 0
            while ( score > highScore && i < topScores.length ) {
                i++
                highScore = topScores[i]
            }
            // i is now the index where score should be inserted
            topScores.splice(i, 0, score)
            topMatches.splice(i, 0, filter)
            // if our match lists are longer than the max remove the lowest
            if (topScores.length > maxMatches) {
                topScores.shift()
                topMatches.shift()
                lowScore = topScores[0]
            }
        }
    }

    // now we want to group the topMatches by paramName
    var groupedMatches = topMatches.reverse().reduce( ( grouped, filter) => {
        let found = grouped.find( g => g.paramName === filter.paramName) 
        found ? found.filters.push(filter)
            : grouped.push({ paramName: filter.paramName, filters : [filter]})
        return grouped
    }, [])

    return groupedMatches
}


/*---------------------------------------- 
 * CONTEXT / MODAL RELATED
 */

export function selectContexts (state) {
    return state.config.contexts
}

export function selectIsModalActive (state) {
    return state.ui.modalId && state.ui.modalId !== "keyword" ? true : false
}


export function selectActiveModal (state) {
    return state.ui.modalId
}


export function selectLastActiveModal (state) {
    return state.ui.lastModal || state.config.filterOrder[0]
}


export function selectDefaultContext (state) {
    return state.config.defaultContext
}

export function selectParamNamesForContext (state, id) {
    var context = selectContextById(state, id)
    if (context.type === NESTED_LIST_CONTROLLER) {
        return [ context.props.parentUrlParam, context.props.childUrlParam ].map( param => {
            return selectParam(state, param).paramName
        })
    } 
    else if (context.type === STACKED_LIST_CONTROLLER) {
        return context.props.groups.map( group => {
            var param = selectParam(state, group.urlParam) || {}
            return param.paramName
        })
    }
    return []
}


export function selectContextByParamName (state, paramName) {
    var contexts = state.config.contexts
    for ( let contextId in contexts) {
        var context = contexts[contextId]
        var paramNames = selectParamNamesForContext(state, context.id)
        if ( paramNames.indexOf(paramName) >= 0) {
            return context.id
        }
    }
}


export function selectModalContextIds (state) {
    var contextIds = state.config.filterOrder
    var contexts = state.config.contexts
    return contextIds.filter( contextId => {
        return MODAL_CONTROLLERS.includes(contexts[contextId].type)
    })
}


export function selectContextById (state, contextId) {
    return state.config.contexts[contextId]
}


export function selectModalIdForParam (state, param) {
    var modalContexts = state.config.contexts 

    /* we want to look through the contexts and see which one
     * should handle this url param
     */

    for ( let contextName in modalContexts) {
        const context = modalContexts[contextName]

        if ( context.type === STACKED_LIST_CONTROLLER ) {
            for ( let group of context.props.groups ) {
                if ( param === group.urlParam ) {
                    return true
                }
            }
        }
        else if ( context.type === NESTED_LIST_CONTROLLER ) {
            if ( param === context.props.parentUrlParam 
                || param === context.props.childUrlParam ) {
                return context.id
            }
        }
    }
   
    // xxx should this be an error?
    throw Error("No context found to handle filter param");
}


/*----------------------------------------
 * URL RELATED
 */

export function selectParam (state, param) {
    return state.config.filterParams.find( p => p.param === param)
}


export function selectQueryObject (state) {
    var activeFilters = selectActiveFilters(state)
    var activeSort = selectActiveSort(state)

    var query = {}

    var groupedFilters = activeFilters.reduce( (groups, f) => {
        groups[f.param] = groups[f.param] || []
        groups[f.param].push(f.value)
        return groups
    }, {})

    for ( let param in groupedFilters ) {
        query[param] = groupedFilters[param].join(',')
    }

    query["sortType"] = activeSort.value

    // xxx need to handle ignored params

    return query
}


/*---------------------------------------- 
 * FILTER CHANGE RELATED
 */

export function selectExclusiveFilterChangeHash (state, filterId) {
    var changeHash = {}
    var isActive = selectIsFilterActive(state, filterId)
    var activeFilters = selectActiveFilters(state)
    activeFilters.forEach( f => {
        changeHash[f.id] = false
    })
    changeHash[filterId] = isActive ? false : true
    return changeHash
}


export function selectSingleFilterChangeHash (state, filterId) {
    var changeHash = {}
    var filter = selectFilterById(state, filterId)
    var activeFilters = selectActiveFilters(state)
    var isActive = selectIsFilterActive(state, filterId)

    activeFilters.forEach( f => {
        if (f.paramName === filter.paramName) {
            changeHash[f.id] = false
        }
    })

    changeHash[filterId] = isActive ? false : true 
}


export function selectMultiFilterChangeHash (state, filterId) {
    var isActive = selectIsFilterActive(state, filterId)

    return {[filterId] : isActive ? false : true }
}


/*---------------------------------------- 
 * FILTER RELATED
 */

export function selectFilterById (state, filterId) {
    return state.data.filters.find( f => f.id === filterId)
}


export function selectFilterType (state, filterId) {
    var filter = selectFilterById(state, filterId)
    var param = selectParam(state, filter.param) || {}
    return param.type || "multi"
}


export function selectIsFilterActive (state, filterId) {
    return state.data.activeFilters[filterId] ? true : false
}


export function selectFiltersByParam (state, filterParam) {
    var filters = state.data.filters 
    var matching = filters.filter(f => f.param === filterParam)
    return matching
}


export function selectActiveFilters (state) {
    var filters = state.data.filters 
    var activeFilters = state.data.activeFilters
    var matching = filters.filter(f => activeFilters[f.id])
    return matching
}


export function selectFiltersByMulti (state, hash) {
    var filters = state.data.filters 
    var keys = Object.keys(hash)
    return filters.filter( f => {
        return keys.reduce( ( flag, key) => {
            return flag && f[key] === hash[key]
        }, true)
    })
}


export function selectFilterChildren (state, filterId) {
    return state.data.filters.filter( f => f.parentId === filterId )
}


export function selectFilterParent (state, filterId) {
    var filter = selectFilterById(state, filterId)
    return state.data.filters.find( f => f.id === filter.parentId )
}



/*---------------------------------------- 
 * UI RELATED 
 */


/*
 * Outputs data strucutre that can be used to render natural language
 */
export function selectTokenGroups (state) {
    var activeFilters = selectActiveFilters(state)
    var contextOrder = state.config.filterOrder

    // tokens are grouped by paramName
    var groups = activeFilters.reduce( (groups, f) => {
        var { paramName, preposition } = f
        var context = selectContextByParamName(state, paramName) || f.context
        var empty = () => { return { paramName, context, preposition, filters : [] }; }
        groups[f.paramName] = groups[f.paramName] || empty()
        groups[f.paramName].filters.push(f)
        return groups;
    }, {})

    groups = Object.values(groups)
    .sort( (a, b) => {
        return contextOrder.indexOf(a.context) > contextOrder.indexOf(b.context)
    })  
    
    return groups
}


/*
 * Outputs data strucutre that can be used to render nested list 
 */
export function selectNestedFilterTree (state, contextId) {
    var context = selectContextById(state, contextId)
    var { parentUrlParam } = context.props
    var parentFilters = selectFiltersByParam( state, parentUrlParam )

    var parentItems = parentFilters.map( f => {
        var isOpen = selectIsNestOpen(state, f.id)
        var children = []

        var parentIsActive = selectIsFilterActive(state, f.id)

        if (isOpen) {
            children = selectFilterChildren(state, f.id).map(c => {
                var isChecked = selectIsFilterActive(state, c.id) || parentIsActive
                var selectionState = isChecked ? "checked" : "unchecked"
                return {
                    selectionState, 
                    id : c.id,
                    value : c.value,
                    name : c.name
                }
            })
        }


        /* reduce to true or false */
        var anyChildrenSelected = children.reduce( (acc, c) => {
            return acc || selectIsFilterActive(state, c.id) 
        }, false)

        var selectionState = [
            parentIsActive ? "checked" : null,
            !parentIsActive && anyChildrenSelected ? "mixed" : null,
            !parentIsActive && !anyChildrenSelected ? "unchecked" : null
        ].filter(v => v)[0]

        return  {
            selectionState, 
            isOpen,
            id : f.id,
            value : f.value,
            name : f.name,
            children
        }
    })
    
    return parentItems
}


export function selectIsNestOpen (state, filterId) {
    return state.ui.openNestedLists[filterId]
}


/*
 * Outputs data strucutre that can be used to render stacked list 
 */
export function selectStackedFilterTree (state, contextId) {
    var context = selectContextById(state, contextId)
    var { groups } = context.props

    return groups.map( g => {
        var items = selectFiltersByParam(state, g.urlParam).map( f => {
            var isChecked = selectIsFilterActive(state, f.id) 
            var selectionState = isChecked ? "checked" : "unchecked"
            return {
                selectionState, 
                id : f.id,
                value : f.value,
                name : f.name
            }
        })
        return {
            title : g.title,
            items
        }
    })
}
